import { ceil, floor } from 'lodash';
import {
    flipPoint,
    pointRelation,
    pointScale,
    pointsOutOfRangeRectangle,
    revertPointY,
    rotatePoint,
    translatePointMove,
} from '@/utils/math-kit';
import { DirectionType, IImageLayerProps, ILayerProps, IPoint, MIN_IMG_SIZE } from '@/version-types';

export const computeAutoScale = (
    stageWidth: number,
    stageHeight: number,
    width: number,
    height: number,
    viewPortPadding: number,
    hasViewPortPadding = true,
) => {
    stageWidth = Math.max(1, stageWidth);
    stageHeight = Math.max(1, stageHeight);
    const padding = hasViewPortPadding ? viewPortPadding : 0;
    const scaleWidth = Math.max(1, stageWidth / width);
    const scaleHeight = Math.max(1, stageHeight / height);
    const widthScale = stageWidth > width ? scaleWidth : (stageWidth - padding) / width;
    const heightScale = stageHeight > height ? scaleHeight : (stageHeight - padding) / height;

    return floor(widthScale < heightScale ? widthScale : heightScale, 2);
};

export const getStageAndMainLayerCenterPoint = (mainLayer: ILayerProps) => {
    const {
        top: originMainLayerTop,
        left: originMainLayerLeft,
        width: originStageWidth,
        height: originStageHeight,
    } = mainLayer;

    const data = mainLayer.data as IImageLayerProps;
    const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;

    const { scale: originMainLayerScale } = imageTransform;

    const stageCenterPoint = {
        x: originStageWidth / 2,
        y: originStageHeight / 2,
    };

    const originMainLayerCenterPoint = {
        x: originMainLayerLeft + (natureMainLayerWidth * originMainLayerScale) / 2,
        y: originMainLayerTop + (natureMainLayerHeight * originMainLayerScale) / 2,
    };

    // 主图中心点和舞台的相对位置
    const originRelativeStagePoint = pointRelation(stageCenterPoint, originMainLayerCenterPoint);

    return {
        stageCenterPoint,
        originMainLayerCenterPoint,
        originRelativeStagePoint,
    };
};

export const getMainLayerCenterPosition = (layer: ILayerProps) => {
    const { data, flipH = false, flipV = false } = layer;
    const { imageTransform, naturalHeight, naturalWidth } = data as IImageLayerProps;
    const { scale } = imageTransform;
    const mainLayerWidth = naturalWidth * scale;
    const mainLayerHeight = naturalHeight * scale;

    return flipPoint(
        {
            x: layer.left + mainLayerWidth / 2,
            y: layer.top + mainLayerHeight / 2,
        },
        flipH,
        flipV,
    );
};

export const getStageCenterPosition = (layer: ILayerProps) => {
    const { width, height } = layer;
    return {
        x: width / 2,
        y: height / 2,
    };
};

export const getLayerCenterPositionRelateToMainLayerCenterPosition = (layer: ILayerProps, mainLayer: ILayerProps) => {
    const mainLayerCenterPosition = getMainLayerCenterPosition(mainLayer);

    const layerCenterPosition = {
        x: layer.left + layer.width / 2,
        y: layer.top + layer.height / 2,
    };

    const relativePosition = {
        x: layerCenterPosition.x - mainLayerCenterPosition.x,
        y: layerCenterPosition.y - mainLayerCenterPosition.y,
    };

    return relativePosition;
};

// 将以主图中心点为原点的坐标转换为以舞台左上角坐标
export const transferMainLayerCenterPointToStagePoint = (mainLayer: ILayerProps, point: IPoint) => {
    const { rotate, flipH = false, flipV = false } = mainLayer;

    return revertPointY(rotatePoint(flipPoint(point, flipH, flipV), -rotate));
};

// 将以舞台左上角坐标转换为主图中心点坐标
export const transferStagePointToMainLayerPoint = (mainLayer: ILayerProps) => {
    const { data, rotate, flipH = false, flipV = false, width, height } = mainLayer;
    const { imageTransform, naturalHeight, naturalWidth } = data as IImageLayerProps;
    const { scale } = imageTransform;
    const mainLayerWidth = naturalWidth * scale;
    const mainLayerHeight = naturalHeight * scale;
    const mainLayerLeft = mainLayer.left;
    const mainLayerTop = mainLayer.top;
    const leftTop = flipPoint(
        rotatePoint(
            revertPointY(
                translatePointMove(
                    {
                        x: 0,
                        y: 0,
                    },
                    -mainLayerLeft - mainLayerWidth / 2,
                    -mainLayerTop - mainLayerHeight / 2,
                ),
            ),
            rotate,
        ),
        flipH,
        flipV,
    );

    const rightTop = flipPoint(
        rotatePoint(
            revertPointY(
                translatePointMove(
                    {
                        x: width,
                        y: 0,
                    },
                    -mainLayerLeft - mainLayerWidth / 2,
                    -mainLayerTop - mainLayerHeight / 2,
                ),
            ),
            rotate,
        ),
        flipH,
        flipV,
    );

    const leftBottom = flipPoint(
        rotatePoint(
            revertPointY(
                translatePointMove(
                    {
                        x: 0,
                        y: height,
                    },
                    -mainLayerLeft - mainLayerWidth / 2,
                    -mainLayerTop - mainLayerHeight / 2,
                ),
            ),
            rotate,
        ),
        flipH,
        flipV,
    );

    const rightBottom = flipPoint(
        rotatePoint(
            revertPointY(
                translatePointMove(
                    {
                        x: width,
                        y: height,
                    },
                    -mainLayerLeft - mainLayerWidth / 2,
                    -mainLayerTop - mainLayerHeight / 2,
                ),
            ),
            rotate,
        ),
        flipH,
        flipV,
    );

    return [leftTop, rightTop, leftBottom, rightBottom];
};

export const getMainLayerConnerPoints = (mainLayer: ILayerProps, stageScale = 1) => {
    const { data } = mainLayer;
    const { imageTransform, naturalHeight, naturalWidth } = data as IImageLayerProps;
    const { scale } = imageTransform;
    const mainLayerWidth = naturalWidth * scale;
    const mainLayerHeight = naturalHeight * scale;

    return {
        leftTop: {
            x: (-mainLayerWidth / 2) * stageScale,
            y: (mainLayerHeight / 2) * stageScale,
        },
        rightTop: {
            x: (mainLayerWidth / 2) * stageScale,
            y: (mainLayerHeight / 2) * stageScale,
        },
        leftBottom: {
            x: (-mainLayerWidth / 2) * stageScale,
            y: (-mainLayerHeight / 2) * stageScale,
        },
        rightBottom: {
            x: (mainLayerWidth / 2) * stageScale,
            y: (-mainLayerHeight / 2) * stageScale,
        },
    };
};

export const isMainLayerOutOfRange = (mainLayer: ILayerProps, isOutOfStage = false) => {
    if (isOutOfStage) {
        return false;
    }
    const isOut = pointsOutOfRangeRectangle(
        transferStagePointToMainLayerPoint(mainLayer),
        getMainLayerConnerPoints(mainLayer),
    );

    return isOut;
};

// 获取图片图层的中心点
export const getCenterPoint = (element: HTMLElement) => {
    const { left, top, width, height } = element.getBoundingClientRect();
    return {
        x: left + width / 2,
        y: top + height / 2,
    };
};

export const getMainLayerCenterPointRelativeStageCenterPoint = (mainLayer: ILayerProps) => {
    const {
        width,
        height,
        left: originMainLayerLeft,
        top: originMainLayerTop,
        flipH = false,
        flipV = false,
    } = mainLayer;
    const data = mainLayer.data as IImageLayerProps;
    const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
    const { scale: originMainLayerScale } = imageTransform;

    const mainLayerSize = {
        width: natureMainLayerWidth * originMainLayerScale,
        height: natureMainLayerHeight * originMainLayerScale,
    };

    const mainLayerCenterPoint = flipPoint(
        revertPointY({
            x: originMainLayerLeft + mainLayerSize.width / 2 - width / 2,
            y: originMainLayerTop + mainLayerSize.height / 2 - height / 2,
        }),
        flipH,
        flipV,
    );

    return mainLayerCenterPoint;
};

/**
 * 以画布中心点缩放主图
 * 旋转的时候是以画布中心点做旋转的 旋转过程中会缩放图层， 也要以画布中心点缩放图层
 */
export const scaleMainLayerCenterPointByStageCenterPoint = (layer: ILayerProps, scale: number) => {
    const { width: stageWidth, height: stageHeight, left: mainLayerLeft, top: mainLayerTop, data } = layer;
    const { imageTransform, naturalWidth, naturalHeight } = data as IImageLayerProps;
    const { scale: mainLayerScale } = imageTransform;

    const stageCenterPoint = {
        x: stageWidth / 2,
        y: stageHeight / 2,
    };

    const mainLayerWidth = naturalWidth * mainLayerScale;
    const mainLayerHeight = naturalHeight * mainLayerScale;
    const mainLayerCenterPoint = {
        x: mainLayerLeft + mainLayerWidth / 2,
        y: mainLayerTop + mainLayerHeight / 2,
    };

    const newMainLayerCenterPoint = pointScale(
        {
            x: mainLayerCenterPoint.x - stageCenterPoint.x,
            y: mainLayerCenterPoint.y - stageCenterPoint.y,
        },
        scale,
    );

    const newMainLayerLeft = stageCenterPoint.x + newMainLayerCenterPoint.x - (mainLayerWidth * scale) / 2;
    const newMainLayerTop = stageCenterPoint.y + newMainLayerCenterPoint.y - (mainLayerHeight * scale) / 2;

    layer.left = newMainLayerLeft;
    layer.top = newMainLayerTop;
};

// 获取鼠标垫位置相对于主图中心点的位置(坐标系是舞台的左上角)
export const getMousePointPositionInMainLayerCenterPoint = (
    mousePointByBrowser: IPoint,
    mainLayer: ILayerProps,
    stageScale: number,
) => {
    const operateStageWrap = document.getElementById('operate-stage-wrap');
    if (!operateStageWrap) {
        return {
            mousePointRelativeStage: {
                x: 0,
                y: 0,
            },
            mousePointRelativeMainLayerCenterPoint: {
                x: 0,
                y: 0,
            },
        };
    }

    const operateStageWrapRect = operateStageWrap.getBoundingClientRect();
    const mousePointRelativeStage = {
        x: (mousePointByBrowser.x - operateStageWrapRect.left) / stageScale,
        y: (mousePointByBrowser.y - operateStageWrapRect.top) / stageScale,
    };

    const mainLayerCenterPoint = getMainLayerCenterPosition(mainLayer);
    const mousePointRelativeMainLayerCenterPoint = {
        x: mousePointRelativeStage.x - mainLayerCenterPoint.x,
        y: mousePointRelativeStage.y - mainLayerCenterPoint.y,
    };

    return { mousePointRelativeStage, mousePointRelativeMainLayerCenterPoint };
};

export const getMainLayerSize = (mainLayer: ILayerProps) => {
    const { data } = mainLayer;
    const { imageTransform, naturalWidth, naturalHeight } = data as IImageLayerProps;
    const { scale: mainLayerScale } = imageTransform;

    const mainLayerWidth = naturalWidth * mainLayerScale;
    const mainLayerHeight = naturalHeight * mainLayerScale;

    return {
        width: mainLayerWidth,
        height: mainLayerHeight,
    };
};

export const toIntByDirection = (value: number, direction: DirectionType) => {
    return direction === DirectionType.POSITIVE ? ceil(value) : floor(value);
};

// 缩放主图
export const scaleMainLayer = (mainLayer: ILayerProps, direction: DirectionType, scaleDistance: number) => {
    const { data } = mainLayer;
    const { imageTransform, naturalWidth, naturalHeight } = data as IImageLayerProps;
    const { scale: mainLayerScale } = imageTransform;

    const mainLayerWidth = naturalWidth * mainLayerScale;
    const mainLayerHeight = naturalHeight * mainLayerScale;

    // // 每缩放一次为10分一
    // const scaleDistance = mainLayerWidth > mainLayerHeight ? ceil(mainLayerWidth / 20) : ceil(mainLayerHeight / 20);

    const newWidth = mainLayerWidth + scaleDistance * direction;

    let newScale = newWidth / naturalWidth;
    if (naturalWidth * newScale < MIN_IMG_SIZE && naturalHeight * newScale < MIN_IMG_SIZE) {
        newScale = MIN_IMG_SIZE / (naturalWidth > naturalHeight ? naturalHeight : naturalWidth);
    } else if (naturalWidth * newScale < MIN_IMG_SIZE) {
        newScale = MIN_IMG_SIZE / naturalWidth;
    } else if (naturalHeight * newScale < MIN_IMG_SIZE) {
        newScale = MIN_IMG_SIZE / naturalHeight;
    }
    imageTransform.scale = newScale;

    return newScale / mainLayerScale;
};

/**
 * 缩放主图后，调整主图位置 不让他超出
 * @param mainLayer
 * @returns 是否可以调整位置
 */
export const adjustMainLayerPositionAfterScaleOut = (mainLayer: ILayerProps) => {
    const mainLayerConnerPoints = getMainLayerConnerPoints(mainLayer);
    // 以主图中心为原点的 画布四个角坐标
    const stagePointRelativeMainLayerCenterPoint = transferStagePointToMainLayerPoint(mainLayer);

    const minX = mainLayerConnerPoints.leftTop.x;
    const maxX = mainLayerConnerPoints.rightBottom.x;
    const minY = mainLayerConnerPoints.rightBottom.y;
    const maxY = mainLayerConnerPoints.leftTop.y;

    // 判断四个角超出了多少  如果左右或者上下两边都超出了 则返回主图
    let overflowX = 0;
    let overflowY = 0;

    for (let i = 0; i < stagePointRelativeMainLayerCenterPoint.length; i++) {
        const point = stagePointRelativeMainLayerCenterPoint[i];
        const overflowMinX = point.x - minX;
        const overflowMaxX = point.x - maxX;
        const overflowMinY = point.y - minY;
        const overflowMaxY = point.y - maxY;
        if (overflowMinX < overflowX) {
            if (overflowX > 0) {
                return false;
            }
            overflowX = overflowMinX;
        }

        if (overflowMaxX > overflowX) {
            if (overflowX < 0) {
                return false;
            }
            overflowX = overflowMaxX;
        }

        if (overflowMinY < overflowY) {
            if (overflowY > 0) {
                return false;
            }
            overflowY = overflowMinY;
        }

        if (overflowMaxY > overflowY) {
            if (overflowY < 0) {
                return false;
            }
            overflowY = overflowMaxY;
        }
    }

    const moveDistance = transferMainLayerCenterPointToStagePoint(mainLayer, {
        x: overflowX,
        y: overflowY,
    });

    mainLayer.left = mainLayer.left + moveDistance.x;
    mainLayer.top = mainLayer.top + moveDistance.y;

    return true;
};

export const stageSizeChangeAndMoveMainLayer = (
    mainLayer: ILayerProps,
    left: number,
    top: number,
    newWidth: number,
    newHeight: number,
) => {
    const mainLayerCenterPosition = getMainLayerCenterPosition(mainLayer);

    const mainLayerSize = getMainLayerSize(mainLayer);
    const newMainLayerLeft = mainLayer.left + left;
    const newMainLayerTop = mainLayer.top + top;

    mainLayer.left = newMainLayerLeft;
    mainLayer.top = newMainLayerTop;
    mainLayer.width = newWidth;
    mainLayer.height = newHeight;
};

export const getLayerCenterPosition = (layer: ILayerProps) => {
    const { left, top, width, height } = layer;
    return {
        x: left + width / 2,
        y: top + height / 2,
    };
};

export const setScaleStageStyle = (
    stageWidth: number,
    stageHeight: number,
    scale: number,
    dataType: string,
    isStageAutoCrop: boolean,
    moveDirection: IPoint,
    originStageSize: { width: number; height: number },
    isEnd: boolean,
) => {
    const style = {
        width: `${stageWidth}px`,
        height: `${stageHeight}px`,
        transform: `scale(${scale}) `,
        flexShrink: 0,
        position: 'relative',
    } as React.CSSProperties;
    const element = document.getElementById('scale-stage-operate');
    if (!element) {
        return;
    }

    const translatePoint = getTranslateToStageCenter(originStageSize.width * scale, originStageSize.height * scale);
    if (isEnd) {
        element.style.width = `${stageWidth}px`;
        element.style.height = `${stageHeight}px`;
        element.style.transform = `translate(${-translatePoint.x}px, ${-translatePoint.y}px)  scale(${scale})`;
        element.style.flexShrink = '0';
        element.style.position = 'relative';
        element.style.transformOrigin = 'left top';
        return;
    }

    if (dataType === 'crop-lc' && isStageAutoCrop) {
        style.transform = `translate(${
            -translatePoint.x - (stageWidth - originStageSize.width) * scale
        }px, ${-translatePoint.y}px) ${style.transform}`;
    } else if ((dataType === 'crop-rc' || dataType === 'crop-bc') && isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x}px, ${-translatePoint.y}px) ${style.transform}`;
    } else if (dataType === 'crop-tc' && isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x}px, ${-translatePoint.y - (stageHeight - originStageSize.height) * scale}px) ${style.transform}`;
    } else if (dataType === 'crop-lc' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x - (stageWidth - originStageSize.width) * scale}px, ${-translatePoint.y - ((stageHeight - originStageSize.height) * scale) / 2}px) ${style.transform}`;
    } else if (dataType === 'crop-rc' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x}px, ${-translatePoint.y + ((stageHeight - originStageSize.height) * scale) / 2}px) ${style.transform}`;
    } else if (dataType === 'crop-tc' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x - ((stageWidth - originStageSize.width) * scale) / 2}px, ${-translatePoint.y - (stageHeight - originStageSize.height) * scale}px) ${style.transform}`;
    } else if (dataType === 'crop-bc' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x + ((stageWidth - originStageSize.width) * scale) / 2}px, ${-translatePoint.y}px) ${style.transform}`;
    } else if (dataType === 'crop-lt' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x - (stageWidth - originStageSize.width) * scale}px, ${-translatePoint.y - (stageHeight - originStageSize.height) * scale}px) ${style.transform}`;
    } else if (dataType === 'crop-rt' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x}px, ${-translatePoint.y - (stageHeight - originStageSize.height) * scale}px) ${style.transform}`;
    } else if (dataType === 'crop-lb' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x - (stageWidth - originStageSize.width) * scale}px, ${-translatePoint.y}px) ${style.transform}`;
    } else if (dataType === 'crop-rb' && !isStageAutoCrop) {
        style.transform = `translate(${-translatePoint.x}px, ${-translatePoint.y}px) ${style.transform}`;
    }

    element.style.width = `${stageWidth}px`;
    element.style.height = `${stageHeight}px`;
    element.style.flexShrink = '0';
    element.style.position = 'relative';
    element.style.transform = style.transform || '';
    element.style.transformOrigin = 'left top';
};

export const getOperateStageStyle = (
    stageWidth: number,
    stageHeight: number,
    scale: number,
    dataType: string,
    isStageAutoCrop: boolean,
    moveDirection: IPoint,
    originStageSize: { width: number; height: number },
    isEnd: boolean,
) => {
    const width = stageWidth * scale;
    const height = stageHeight * scale;
    const style = {
        width,
        height,
        position: 'relative',
        boxShadow: `0 0 0 1px var(--stroke-cursor-target-color),0 0 0 ${10000}px rgba(255,255,255,0.5)`,
    } as React.CSSProperties;

    const element = document.getElementById('operate-stage-wrap');
    if (!element) {
        return;
    }

    const translatePoint = getTranslateToStageCenter(originStageSize.width * scale, originStageSize.height * scale);
    let translateX = -translatePoint.x;
    let translateY = -translatePoint.y;

    if (isEnd) {
        element.style.width = `${width}px`;
        element.style.height = `${height}px`;
        element.style.position = `relative`;
        element.style.boxShadow = `0 0 0 1px var(--stroke-cursor-target-color),0 0 0 ${10000}px rgba(255,255,255,0.5)`;
        element.style.transform = `translate(${translateX}px, ${translateY}px)`;
        element.style.transformOrigin = 'left top';
        return {
            width,
            height,
            translateX,
            translateY,
        };
    }

    if (dataType === 'crop-lc' && isStageAutoCrop) {
        translateX = -translatePoint.x - (stageWidth - originStageSize.width) * scale;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if ((dataType === 'crop-rc' || dataType === 'crop-bc') && isStageAutoCrop) {
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-tc' && isStageAutoCrop) {
        translateY = -translatePoint.y - (stageHeight - originStageSize.height) * scale;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-lc' && !isStageAutoCrop) {
        translateX = -translatePoint.x - (stageWidth - originStageSize.width) * scale;
        translateY = -translatePoint.y - ((stageHeight - originStageSize.height) * scale) / 2;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-rc' && !isStageAutoCrop) {
        translateY = -translatePoint.y + ((stageHeight - originStageSize.height) * scale) / 2;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-tc' && !isStageAutoCrop) {
        translateX = -translatePoint.x - ((stageWidth - originStageSize.width) * scale) / 2;
        translateY = -translatePoint.y - (stageHeight - originStageSize.height) * scale;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-bc' && !isStageAutoCrop) {
        translateX = -translatePoint.x + ((stageWidth - originStageSize.width) * scale) / 2;
        translateY = -translatePoint.y - (stageHeight - originStageSize.height) * scale;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-lt' && !isStageAutoCrop) {
        translateX = -translatePoint.x - (stageWidth - originStageSize.width) * scale;
        translateY = -translatePoint.y - (stageHeight - originStageSize.height) * scale;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-rt' && !isStageAutoCrop) {
        translateY = -translatePoint.y - (stageHeight - originStageSize.height) * scale;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-lb' && !isStageAutoCrop) {
        translateX = -translatePoint.x - (stageWidth - originStageSize.width) * scale;
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    } else if (dataType === 'crop-rb' && !isStageAutoCrop) {
        style.transform = `translate(${translateX}px, ${translateY}px)`;
    }

    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    element.style.position = `relative`;
    element.style.boxShadow = `0 0 0 1px var(--stroke-cursor-target-color),0 0 0 ${10000}px rgba(255,255,255,0.5)`;
    element.style.transform = style.transform || '';
    element.style.transformOrigin = 'left top';

    return {
        width,
        height,
        translateX,
        translateY,
    };
};

export const getTranslateToStageCenter = (stageWidth: number, stageHeight: number) => {
    const stageElement = document.getElementById('crop-stage-wrap');
    if (!stageElement) {
        return {
            x: 0,
            y: 0,
        };
    }

    const stageElementRect = stageElement.getBoundingClientRect();
    const moveElementCenterPoint = {
        x: stageWidth / 2,
        y: stageHeight / 2,
    };
    const stageElementCenterPoint = {
        x: stageElementRect.width / 2,
        y: stageElementRect.height / 2,
    };
    return {
        x: moveElementCenterPoint.x - stageElementCenterPoint.x,
        y: moveElementCenterPoint.y - stageElementCenterPoint.y,
    };
};
